import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'
import { getLedgerSafeActiveMapping } from './ledgerSafeBoundaryMapping'
import { buildCanonicalSplitDisplayWords } from './ledgerSafeBoundaryText'
import {
  buildLedgerSafeWitnessContextAnalysis,
  buildLedgerSafeWitnessSourceContext,
  type LedgerSafeResolvedMappedRow,
  type LedgerSafeWitnessContextAnalysis
} from './ledgerSafeBoundaryContext'

export interface LedgerSafeEditabilityBlockedRow {
  rowId: string
  unitUuid?: string
  reason: string
}

export interface LedgerSafeEditabilityBlockedBoundary {
  leftRowId: string
  rightRowId: string | null
  leftUnitUuid?: string
  rightUnitUuid?: string
  reason: string
}

export interface LedgerSafeWitnessEditabilityAudit {
  witnessId: string
  mappedRowCount: number
  editableSplitRowIds: string[]
  blockedSplitRows: LedgerSafeEditabilityBlockedRow[]
  editableBoundaryLeftRowIds: string[]
  blockedBoundaries: LedgerSafeEditabilityBlockedBoundary[]
  fullyEditable: boolean
}

const getBaseBlockedReason = ({
  segmentationResult,
  sourceText
}: {
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): string => {
  if (!segmentationResult) {
    return 'No segmentation result is available for this witness'
  }
  if (!sourceText) {
    return 'No source text is available for this witness'
  }
  return ''
}

export const explainLedgerSafeSplitBlock = ({
  mappedRow,
  baseBlockedReason,
  sourceContext
}: {
  mappedRow: LedgerSafeResolvedMappedRow
  baseBlockedReason: string
  sourceContext: ReturnType<typeof buildLedgerSafeWitnessSourceContext>
}): string => {
  if (baseBlockedReason) {
    return baseBlockedReason
  }
  const orderedIndex = mappedRow.orderedIndex
  if (typeof orderedIndex !== 'number') {
    return 'Mapped unit is not present in the witness segmentation'
  }
  const unitEntry = sourceContext.orderedUnits[orderedIndex]
  const wordInfo = sourceContext.wordInfoByOrderedIndex.get(orderedIndex)
  if (!unitEntry || !wordInfo) {
    return 'Mapped unit has no segmentable source words'
  }
  if (wordInfo.endIndex <= wordInfo.startIndex) {
    return 'Mapped unit has fewer than two segmentable words'
  }
  const splitWords = buildCanonicalSplitDisplayWords({
    unitText: String(unitEntry.unit.unit_text_content || ''),
    sourceText: sourceContext.sourceText,
    wordInfo,
    segmentableWordSpans: sourceContext.segmentableWordSpans
  })
  if (!splitWords) {
    return 'Mapped unit text cannot be projected back to its source span'
  }
  return 'Split context is not available for this mapped unit'
}

export const explainLedgerSafeBoundaryBlock = ({
  left,
  right,
  baseBlockedReason,
  sourceContext
}: {
  left: LedgerSafeResolvedMappedRow
  right: LedgerSafeResolvedMappedRow | null
  baseBlockedReason: string
  sourceContext: ReturnType<typeof buildLedgerSafeWitnessSourceContext>
}): string => {
  if (baseBlockedReason) {
    return baseBlockedReason
  }
  if (!right) {
    return 'No later mapped unit is available in this witness'
  }
  const leftOrderedIndex = left.orderedIndex
  const rightOrderedIndex = right.orderedIndex
  if (typeof leftOrderedIndex !== 'number' || typeof rightOrderedIndex !== 'number') {
    return 'One or both mapped units are not present in the witness segmentation'
  }
  if (rightOrderedIndex !== leftOrderedIndex + 1) {
    return rightOrderedIndex < leftOrderedIndex
      ? 'Mapped units are transposed relative to witness source order'
      : 'An unplaced mapped unit sits between these rows in the witness segmentation'
  }
  const leftUnitEntry = sourceContext.orderedUnits[leftOrderedIndex]
  const rightUnitEntry = sourceContext.orderedUnits[rightOrderedIndex]
  if (!leftUnitEntry || !rightUnitEntry) {
    return 'Unable to resolve mapped units in the witness segmentation'
  }
  const leftWordInfo = sourceContext.wordInfoByOrderedIndex.get(leftOrderedIndex)
  const rightWordInfo = sourceContext.wordInfoByOrderedIndex.get(rightOrderedIndex)
  if (!leftWordInfo || !rightWordInfo) {
    return 'One or both mapped units have no segmentable source words'
  }
  if (rightWordInfo.startIndex !== leftWordInfo.endIndex + 1) {
    return 'Mapped units do not share a contiguous source boundary'
  }
  return 'Boundary context is not available for these mapped units'
}

export const auditLedgerSafeWitnessEditabilityFromAnalysis = ({
  analysis,
  segmentationResult,
  sourceText
}: {
  analysis: LedgerSafeWitnessContextAnalysis
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeWitnessEditabilityAudit => {
  const {
    witnessId,
    sortedRows,
    sourceContext,
    mappedRows,
    preparedContext: prepared
  } = analysis
  const baseBlockedReason = getBaseBlockedReason({ segmentationResult, sourceText })

  const editableSplitRowIds: string[] = []
  const blockedSplitRows: LedgerSafeEditabilityBlockedRow[] = []
  mappedRows.forEach(mappedRow => {
    const { row, unitUuid } = mappedRow
    if (prepared?.splitContextsByRowId.has(row.id)) {
      editableSplitRowIds.push(row.id)
      return
    }
    blockedSplitRows.push({
      rowId: row.id,
      unitUuid,
      reason: explainLedgerSafeSplitBlock({
        mappedRow,
        baseBlockedReason,
        sourceContext
      })
    })
  })

  const editableBoundaryLeftRowIds: string[] = []
  const blockedBoundaries: LedgerSafeEditabilityBlockedBoundary[] = []
  mappedRows.forEach((left, index) => {
    const right = mappedRows[index + 1] || null
    if (!right) {
      return
    }
    const context = prepared?.boundaryContextsByLeftRowId.get(left.row.id)
    if (context && context.rightRowId === right.row.id) {
      editableBoundaryLeftRowIds.push(left.row.id)
      return
    }
    blockedBoundaries.push({
      leftRowId: left.row.id,
      rightRowId: right.row.id,
      leftUnitUuid: left.originalUnitUuid,
      rightUnitUuid: right.originalUnitUuid,
      reason: explainLedgerSafeBoundaryBlock({
        left,
        right,
        baseBlockedReason,
        sourceContext
      })
    })
  })

  const hasActiveMappings = sortedRows.some(row => Boolean(getLedgerSafeActiveMapping(row, witnessId)?.unitUuid))

  return {
    witnessId,
    mappedRowCount: mappedRows.length,
    editableSplitRowIds,
    blockedSplitRows,
    editableBoundaryLeftRowIds,
    blockedBoundaries,
    fullyEditable: hasActiveMappings && blockedSplitRows.length === 0 && blockedBoundaries.length === 0
  }
}

export const auditLedgerSafeWitnessEditability = ({
  rows,
  witnessId,
  segmentationResult,
  sourceText
}: {
  rows: AnchoredRow[]
  witnessId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeWitnessEditabilityAudit => auditLedgerSafeWitnessEditabilityFromAnalysis({
  analysis: buildLedgerSafeWitnessContextAnalysis({
    rows,
    witnessId,
    segmentationResult,
    sourceText
  }),
  segmentationResult,
  sourceText
})
